import { cn } from '../../lib/utils';

/** Ops dashboard accent zones */
export type HealthZone = 'people' | 'mail' | 'infrastructure' | 'security';

export type ChipTone = 'good' | 'warn' | 'bad' | 'neutral' | 'sky' | 'violet';

export type OpsIntensity = 'admin' | 'user';

const zoneConfig: Record<
  HealthZone,
  {
    accentBorder: string;
    cardTintAdmin: string;
    cardTintUser: string;
    headerDot: string;
    statTile: string;
    statTileLabel: string;
    tableHeader: string;
    tableRowActive: string;
    tableRowZebra: string;
    linkAccent: string;
  }
> = {
  people: {
    accentBorder: 'border-l-violet-500',
    cardTintAdmin: 'bg-violet-50/40 dark:bg-violet-950/20',
    cardTintUser: 'bg-violet-50/60 dark:bg-violet-950/15',
    headerDot: 'bg-violet-500',
    statTile: 'border-violet-200/70 bg-violet-50/70 dark:border-violet-800/40 dark:bg-violet-950/30',
    statTileLabel: 'text-violet-600/80 dark:text-violet-300/80',
    tableHeader: 'bg-violet-100/80 dark:bg-violet-900/35 text-violet-900/70 dark:text-violet-200/80',
    tableRowActive: 'border-l-2 border-l-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20',
    tableRowZebra: 'even:bg-violet-50/30 dark:even:bg-violet-950/10',
    linkAccent: 'text-violet-700 dark:text-violet-300 hover:text-violet-900 dark:hover:text-violet-100',
  },
  mail: {
    accentBorder: 'border-l-sky-500',
    cardTintAdmin: 'bg-sky-50/40 dark:bg-sky-950/20',
    cardTintUser: 'bg-sky-50/60 dark:bg-sky-950/15',
    headerDot: 'bg-sky-500',
    statTile: 'border-sky-200/70 bg-sky-50/70 dark:border-sky-800/40 dark:bg-sky-950/30',
    statTileLabel: 'text-sky-600/80 dark:text-sky-300/80',
    tableHeader: 'bg-sky-100/80 dark:bg-sky-900/35 text-sky-900/70 dark:text-sky-200/80',
    tableRowActive: '',
    tableRowZebra: 'even:bg-sky-50/30 dark:even:bg-sky-950/10',
    linkAccent: 'text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-100',
  },
  infrastructure: {
    accentBorder: 'border-l-slate-400 dark:border-l-slate-500',
    cardTintAdmin: 'bg-slate-50/50 dark:bg-slate-900/30',
    cardTintUser: 'bg-slate-50/60 dark:bg-slate-900/25',
    headerDot: 'bg-slate-400 dark:bg-slate-500',
    statTile: 'border-slate-200/80 bg-slate-50/80 dark:border-slate-700/50 dark:bg-slate-900/40',
    statTileLabel: 'text-slate-500 dark:text-slate-400',
    tableHeader: 'bg-slate-100/80 dark:bg-slate-800/50',
    tableRowActive: '',
    tableRowZebra: 'even:bg-slate-50/40 dark:even:bg-slate-900/20',
    linkAccent: 'text-slate-700 dark:text-slate-300',
  },
  security: {
    accentBorder: 'border-l-amber-500',
    cardTintAdmin: 'bg-amber-50/35 dark:bg-amber-950/15',
    cardTintUser: 'bg-amber-50/50 dark:bg-amber-950/10',
    headerDot: 'bg-amber-500',
    statTile: 'border-amber-200/70 bg-amber-50/70 dark:border-amber-800/40 dark:bg-amber-950/25',
    statTileLabel: 'text-amber-700/80 dark:text-amber-300/80',
    tableHeader: 'bg-amber-100/80 dark:bg-amber-900/30',
    tableRowActive: '',
    tableRowZebra: 'even:bg-amber-50/25 dark:even:bg-amber-950/10',
    linkAccent: 'text-amber-800 dark:text-amber-300',
  },
};

export function zoneCardClass(zone: HealthZone | undefined, intensity: OpsIntensity = 'admin'): string {
  if (!zone) return '';
  const cfg = zoneConfig[zone];
  return cn('border-l-[3px]', cfg.accentBorder, intensity === 'user' ? cfg.cardTintUser : cfg.cardTintAdmin);
}

export function zoneDotClass(zone: HealthZone): string {
  return cn('inline-block h-2 w-2 shrink-0 rounded-full', zoneConfig[zone].headerDot);
}

export function zoneStatTileClass(zone: HealthZone): string {
  return cn('rounded-lg border px-3 py-2.5', zoneConfig[zone].statTile);
}

export function zoneStatTileLabelClass(zone: HealthZone): string {
  return cn('text-[10px] uppercase tracking-wider font-semibold', zoneConfig[zone].statTileLabel);
}

export function zoneTableHeaderClass(zone: HealthZone): string {
  return cn('text-left text-[10px] uppercase tracking-wider font-semibold', zoneConfig[zone].tableHeader);
}

export function zoneTableRowClass(zone: HealthZone, opts?: { active?: boolean; zebra?: boolean }): string {
  const cfg = zoneConfig[zone];
  return cn(opts?.active && cfg.tableRowActive, opts?.zebra && cfg.tableRowZebra);
}

export function zoneLinkClass(zone: HealthZone): string {
  return cn('text-sm font-medium transition-colors', zoneConfig[zone].linkAccent);
}

export function infrastructureBandClass(): string {
  return cn(
    'rounded-2xl border border-slate-200/80 dark:border-slate-700/60',
    'bg-slate-100/40 dark:bg-slate-900/25 p-4 md:p-5 flex flex-col gap-6',
  );
}

export function infrastructureBandLabelClass(): string {
  return 'text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 px-1';
}

export const chipToneStyles: Record<ChipTone, { shell: string; value: string }> = {
  good: {
    shell: 'border-emerald-200/80 bg-emerald-50/70 dark:border-emerald-800/50 dark:bg-emerald-950/30',
    value: 'text-emerald-800 dark:text-emerald-300',
  },
  warn: {
    shell: 'border-amber-200/80 bg-amber-50/70 dark:border-amber-800/50 dark:bg-amber-950/30',
    value: 'text-amber-800 dark:text-amber-300',
  },
  bad: {
    shell: 'border-rose-200/80 bg-rose-50/70 dark:border-rose-800/50 dark:bg-rose-950/30',
    value: 'text-rose-800 dark:text-rose-300',
  },
  neutral: {
    shell: 'border-slate-200/80 bg-slate-50/70 dark:border-slate-700/50 dark:bg-slate-900/35',
    value: 'text-slate-800 dark:text-slate-200',
  },
  sky: {
    shell: 'border-sky-200/80 bg-sky-50/70 dark:border-sky-800/50 dark:bg-sky-950/30',
    value: 'text-sky-800 dark:text-sky-300',
  },
  violet: {
    shell: 'border-violet-200/80 bg-violet-50/70 dark:border-violet-800/50 dark:bg-violet-950/30',
    value: 'text-violet-800 dark:text-violet-300',
  },
};

export function heroStatusClass(status: 'ok' | 'warning' | 'error', intensity: OpsIntensity = 'admin'): string {
  if (status === 'ok') {
    return intensity === 'user'
      ? 'bg-gradient-to-br from-emerald-500/10 via-transparent to-sky-500/5'
      : 'bg-gradient-to-br from-emerald-500/15 via-transparent to-sky-500/8';
  }
  if (status === 'warning') {
    return intensity === 'user'
      ? 'bg-gradient-to-br from-amber-500/10 via-transparent to-transparent'
      : 'bg-gradient-to-br from-amber-500/15 via-transparent to-transparent';
  }
  return intensity === 'user'
    ? 'bg-gradient-to-br from-rose-500/10 via-transparent to-transparent'
    : 'bg-gradient-to-br from-rose-500/15 via-transparent to-transparent';
}

export function resourcesPanelClass(): string {
  return cn(
    'rounded-xl border border-slate-200/60 dark:border-slate-700/50',
    'bg-slate-50/50 dark:bg-slate-900/20 p-4 md:p-5',
  );
}
